// src/pages/CustomReport.js
import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Flex,
  Button,
  Spinner,
  Text,
  Heading,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import {
  GetAllCustomReports,
  DeleteCustomReport,
  GenerateCustomReportFile,
} from '../api/customReport';

const CustomReport = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [generatingId, setGeneratingId] = useState(null);

  const {
    data: reports,
    isLoading,
    error,
  } = useQuery('customReports', GetAllCustomReports);

  const handleDelete = async id => {
    try {
      await DeleteCustomReport(id);
      await queryClient.invalidateQueries('customReports');
      toast({
        title: 'Report deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error deleting report',
        description: err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleGenerate = async id => {
    try {
      setGeneratingId(id);
      await GenerateCustomReportFile(id);
      toast({
        title: 'Report generated',
        description: 'Download should start shortly.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error generating report',
        description: err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setGeneratingId(null);
    }
  };

  if (isLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box w="100%" p={5}>
        <Text color="red.500">Error loading reports: {error.message}</Text>
      </Box>
    );
  }

  return (
    <Box w="100%" p={5}>
      <Heading size="md" mb={4}>
        Custom Reports
      </Heading>

      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Enabled</Th>
              <Th>Name</Th>
              <Th>Period</Th>
              <Th textAlign="right">Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {reports?.map(report => (
              <Tr key={report.id}>
                <Td>
                  <Badge colorScheme={report.enabled ? 'green' : 'red'}>
                    {report.enabled ? 'ENABLED' : 'DISABLED'}
                  </Badge>
                </Td>
                <Td>{report.name}</Td>
                <Td>{report.period}</Td>
                <Td textAlign="right">
                  <Flex justifyContent="flex-end" gap={3}>
                    <Text
                      as="span"
                      cursor="pointer"
                      color="blue.500"
                      onClick={() =>
                        navigate(`/settings/custom-reports/${report.id}`)
                      }
                    >
                      Edit
                    </Text>
                    <Text
                      as="span"
                      cursor="pointer"
                      color="red.500"
                      onClick={() => handleDelete(report.id)}
                    >
                      Delete
                    </Text>
                    {generatingId === report.id ? (
                      <Spinner size="sm" color="teal.600" />
                    ) : (
                      <Text
                        as="span"
                        cursor="pointer"
                        color="teal.600"
                        onClick={() => handleGenerate(report.id)}
                      >
                        Generate
                      </Text>
                    )}
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Flex justifyContent="flex-end" mt={4}>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => navigate('/settings/custom-reports/new')}
        >
          Add Custom Report
        </Button>
      </Flex>
    </Box>
  );
};

export default CustomReport;
